/**
 * RecentAutoLockoutsCard — Super Admin widget added in iter81 (Feb 21, 2026)
 *
 * Renders the last 24h of auto-lockout audit events in a compact card. Gives
 * ops teams an instant heads-up if a brute-force sweep hit a legit user, plus
 * whether an admin has already unarchived them.
 *
 * Backend: GET /api/admin/auto-lockouts/recent (see admin_audit_routes.py).
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, ShieldCheck, RefreshCw, ChevronRight, UserCheck } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';

const relTime = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export default function RecentAutoLockoutsCard({ onViewUser }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unarchiving, setUnarchiving] = useState({}); // { [targetId]: true }

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiCall('/api/admin/auto-lockouts/recent?hours=24&limit=10');
      setData(res);
    } catch (e) {
      toast.error(e.message || 'Failed to load recent lockouts');
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (targetId, email) => {
    if (!targetId) return;
    if (!window.confirm(`Unarchive ${email || 'this user'}? They'll be able to sign in again immediately.`)) return;
    setUnarchiving((prev) => ({ ...prev, [targetId]: true }));
    try {
      await apiCall(`/api/admin/users/${targetId}/unarchive`, { method: 'POST' });
      toast.success(`${email || 'User'} unarchived`);
      // Optimistic: flip still_locked on the item locally so the pill
      // switches from red to green without a full refetch flash.
      setData((prev) => prev ? ({
        ...prev,
        items: prev.items.map((i) => i.target_id === targetId ? { ...i, still_locked: false } : i),
      }) : prev);
    } catch (e) {
      toast.error(e.message || 'Failed to unarchive user');
    } finally {
      setUnarchiving((prev) => {
        const next = { ...prev };
        delete next[targetId];
        return next;
      });
    }
  };

  useEffect(() => {
    load();
    // Poll every 60s so the ops team sees new lockouts without a refresh.
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const items = data?.items || [];
  const total = data?.total || 0;
  const anyStillLocked = items.some((i) => i.still_locked);

  return (
    <div
      className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition"
      data-testid="recent-auto-lockouts-card"
    >
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className={`p-2.5 rounded-xl text-white flex-shrink-0 ${
              anyStillLocked
                ? 'bg-gradient-to-br from-red-500 to-orange-500'
                : 'bg-gradient-to-br from-slate-500 to-slate-600'
            }`}
          >
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-gray-900 truncate">Recent Auto-Lockouts</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Accounts our brute-force protection archived in the last 24h
            </p>
          </div>
        </div>
        <button
          onClick={load}
          className="p-2 rounded-lg hover:bg-gray-100"
          data-testid="recent-auto-lockouts-refresh"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && !data ? (
        <div className="py-6 flex justify-center">
          <div className="w-6 h-6 border-2 border-slate-200 border-t-slate-600 rounded-full animate-spin" />
        </div>
      ) : total === 0 ? (
        <div
          className="text-center py-8 rounded-xl bg-emerald-50 border border-emerald-100"
          data-testid="recent-auto-lockouts-empty"
        >
          <ShieldCheck className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
          <p className="text-sm font-semibold text-emerald-800">All clear</p>
          <p className="text-xs text-emerald-700 mt-0.5">
            No auto-lockouts triggered in the last 24 hours.
          </p>
        </div>
      ) : (
        <>
          <div
            className="flex items-baseline gap-3 mb-3 pb-3 border-b border-gray-100"
            data-testid="recent-auto-lockouts-summary"
          >
            <div>
              <p className="text-3xl font-bold text-gray-900">{total}</p>
              <p className="text-xs text-gray-500">total in window</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-2xl font-bold text-red-600" data-testid="recent-auto-lockouts-still-locked-count">
                {items.filter((i) => i.still_locked).length}
              </p>
              <p className="text-xs text-gray-500">still locked</p>
            </div>
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {items.map((it) => (
              <div
                key={`${it.target_id}-${it.locked_at}`}
                className={`flex items-center gap-3 p-2.5 rounded-lg text-sm border ${
                  it.still_locked
                    ? 'bg-red-50 border-red-100'
                    : 'bg-emerald-50 border-emerald-100'
                }`}
                data-testid="recent-auto-lockouts-item"
              >
                <div
                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    it.still_locked ? 'bg-red-500' : 'bg-emerald-500'
                  }`}
                />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-900 truncate">
                    {it.email || '(no email)'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {it.role} · {it.fail_count} fails in {it.window_minutes}m · {relTime(it.locked_at)}
                  </p>
                </div>
                <span
                  className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full flex-shrink-0 ${
                    it.still_locked
                      ? 'bg-red-100 text-red-700 border border-red-200'
                      : 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                  }`}
                >
                  {it.still_locked ? 'Locked' : 'Restored'}
                </span>
                {it.still_locked && (
                  <button
                    onClick={() => handleUnarchive(it.target_id, it.email)}
                    disabled={!!unarchiving[it.target_id]}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-wait flex-shrink-0"
                    data-testid={`recent-auto-lockouts-unarchive-${it.target_id}`}
                    title="Unarchive this user"
                  >
                    <UserCheck className="w-3 h-3" />
                    {unarchiving[it.target_id] ? 'Restoring…' : 'Unarchive'}
                  </button>
                )}
                {onViewUser && (
                  <button
                    onClick={() => onViewUser(it.target_id)}
                    className="p-1 rounded hover:bg-white flex-shrink-0"
                    title="View user activity"
                    data-testid={`recent-auto-lockouts-view-${it.target_id}`}
                  >
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
